/**
 * Sibling wait coherence for locked/editor macro trees.
 *
 * Product rule: waits may have siblings. If any child under a parent is a wait
 * (`kind: 'wait'` or `waitEnabled`), every sibling must be wait-capable and share
 * the same waitIntervalMs / waitTimeoutMs. Screenshot-wait siblings and dedicated
 * Wait siblings are separate topologies (not mixed under one parent).
 */

export type WaitClockPair = { intervalMs: number; timeoutMs: number };

export type WaitSiblingNode = {
  id?: string;
  kind?: string;
  waitEnabled?: boolean;
  waitIntervalMs?: number;
  waitTimeoutMs?: number;
  expectedState?: { snapshots?: readonly unknown[] } | null;
};

export function isDedicatedWait(node: WaitSiblingNode): boolean {
  return node.kind === 'wait';
}

export function isScreenshotWaitNode(node: WaitSiblingNode): boolean {
  return (
    node.kind !== 'wait' &&
    node.waitEnabled === true &&
    (node.expectedState?.snapshots?.length ?? 0) >= 1
  );
}

export function isWaitCapable(node: WaitSiblingNode): boolean {
  return isDedicatedWait(node) || node.waitEnabled === true;
}

export function readWaitClocks(node: WaitSiblingNode): WaitClockPair | null {
  if (node.waitIntervalMs === undefined || node.waitTimeoutMs === undefined) return null;
  return { intervalMs: node.waitIntervalMs, timeoutMs: node.waitTimeoutMs };
}

export type WaitSiblingCoherenceIssue =
  | { code: 'mixed_wait_modes'; message: string }
  | { code: 'wait_sibling_not_wait_capable'; message: string; nodeId: string }
  | { code: 'wait_clocks_missing'; message: string; nodeId: string }
  | { code: 'wait_clocks_mismatch'; message: string };

/**
 * When siblings.length >= 2 and any sibling is wait-capable, enforce coherence.
 * Returns issues (empty = ok). Callers decide error vs lock failure.
 */
export function validateWaitSiblingCoherence(
  parentId: string,
  siblings: readonly WaitSiblingNode[],
): WaitSiblingCoherenceIssue[] {
  if (siblings.length < 2) return [];
  if (!siblings.some(isWaitCapable)) return [];

  const issues: WaitSiblingCoherenceIssue[] = [];
  const dedicated = siblings.filter(isDedicatedWait);
  const screenshotWaits = siblings.filter(isScreenshotWaitNode);
  const other = siblings.filter((n) => !isDedicatedWait(n) && !isScreenshotWaitNode(n));

  if (other.length > 0) {
    for (const node of other) {
      issues.push({
        code: 'wait_sibling_not_wait_capable',
        nodeId: node.id ?? parentId,
        message: `node ${node.id ?? '(unknown)'} is a sibling of a wait under ${parentId} but is not wait-capable — every sibling must be a Wait node or screenshot-wait`,
      });
    }
  }

  if (dedicated.length > 0 && screenshotWaits.length > 0) {
    issues.push({
      code: 'mixed_wait_modes',
      message: `parent ${parentId} mixes dedicated Wait nodes with screenshot-wait siblings — use one wait mode per sibling set`,
    });
  }

  if (issues.length > 0) return issues;

  const waitSet = dedicated.length > 0 ? dedicated : siblings.filter((n) => n.waitEnabled === true);
  let expected: WaitClockPair | null = null;
  for (const node of waitSet) {
    const clocks = readWaitClocks(node);
    if (!clocks) {
      issues.push({
        code: 'wait_clocks_missing',
        nodeId: node.id ?? parentId,
        message: `node ${node.id ?? '(unknown)'} is a wait sibling under ${parentId} but is missing waitIntervalMs/waitTimeoutMs`,
      });
      continue;
    }
    if (!expected) {
      expected = clocks;
      continue;
    }
    if (clocks.intervalMs !== expected.intervalMs || clocks.timeoutMs !== expected.timeoutMs) {
      issues.push({
        code: 'wait_clocks_mismatch',
        message: `wait siblings under ${parentId} must share the same waitIntervalMs/waitTimeoutMs (found ${clocks.intervalMs}/${clocks.timeoutMs} vs ${expected.intervalMs}/${expected.timeoutMs})`,
      });
      break;
    }
  }

  return issues;
}
